import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
    IconButton,
    Paper,
} from '@material-ui/core';
import { Add, Edit, Delete } from '@material-ui/icons';
import { ResponsiveContainer, Tooltip as RTooltip, Legend, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { useStyles } from './cashflow.styles';
import { categoryColor } from './cashflow.constants';
import { fmt, computeCategoryEoyProjections, getPeriodWindow as getPeriodWindowUtil } from './cashflow.utils';
import { CategoryLimit } from 'core/services/lists.service';
import { getCategorySpending, getEoyProjections } from 'core/services/tasks.service';

interface StatsTabProps {
    entries: any[];
    selectedList: any;
    updateListLimitsSignal: any;
    isMobile: boolean;
    userId: string;
    onNavigateToEntries: () => void;
    setEntrySearch: (v: string) => void;
    setEntryTypeFilter: (v: 'all' | 'DEBIT' | 'CREDIT') => void;
    setEntryCategoryFilter: (v: string) => void;
    setEntryTagFilter: (v: string) => void;
    setEntryDateFrom: (v: string) => void;
    setEntryDateTo: (v: string) => void;
}

export const StatsTab: React.FC<StatsTabProps> = ({
    entries,
    selectedList,
    updateListLimitsSignal,
    isMobile,
    userId,
    onNavigateToEntries,
    setEntrySearch,
    setEntryTypeFilter,
    setEntryCategoryFilter,
    setEntryTagFilter,
    setEntryDateFrom,
    setEntryDateTo,
}) => {
    const classes = useStyles();

    // Category limit dialog state
    const [showLimitDialog, setShowLimitDialog] = useState(false);
    const [limitDialogField, setLimitDialogField] = useState<'category' | 'tag'>('category');
    const [limitDialogCategory, setLimitDialogCategory] = useState('');
    const [limitDialogAmount, setLimitDialogAmount] = useState('');
    const [limitDialogInterval, setLimitDialogInterval] = useState<'weekly' | 'monthly' | 'quarterly' | 'yearly'>('monthly');
    const [limitDialogEditIdx, setLimitDialogEditIdx] = useState<number | null>(null);

    const categoryLimits: CategoryLimit[] = useMemo(() => selectedList?.categoryLimits || [], [selectedList]);

    // Computed from entries
    const allCategories = useMemo(
        () => Array.from(new Set(entries.map((e: any) => e.category).filter(Boolean))).sort() as string[],
        [entries]
    );
    const allTags = useMemo(
        () => Array.from(new Set(
            entries.flatMap((e: any) => e.tag ? e.tag.split(',').map((s: string) => s.trim()).filter(Boolean) : [])
        )).sort() as string[],
        [entries]
    );

    const inflows = useMemo(() => entries.filter((e: any) => e.type === 'CREDIT' && e.date), [entries]);
    const bills   = useMemo(() => entries.filter((e: any) => e.type === 'DEBIT' && e.date),   [entries]);

    // Period window helper — delegates to shared utility
    const getPeriodWindow = (interval: string) => getPeriodWindowUtil(interval);

    // Category budget trackers — fetched from API
    const [categoryLimitTrackers, setCategoryLimitTrackers] = useState<
        (CategoryLimit & { spent: number; pct: number; idx: number })[]
    >([]);

    const fetchCategorySpending = useCallback(async () => {
        if (!userId || !selectedList?.id || categoryLimits.length === 0) {
            setCategoryLimitTrackers([]);
            return;
        }
        try {
            // Fetch spending for each distinct interval used by category limits
            const intervals = Array.from(new Set(categoryLimits.map(l => l.interval)));
            const spendingByInterval: Record<string, any[]> = {};
            await Promise.all(intervals.map(async (interval) => {
                const res = await getCategorySpending(userId, selectedList.id, interval);
                spendingByInterval[interval] = res.spending || [];
            }));

            const trackers = categoryLimits.map((limit, idx) => {
                const apiTrackers = spendingByInterval[limit.interval] || [];
                const match = apiTrackers.find((t: any) =>
                    t.category.toLowerCase() === limit.category.toLowerCase()
                );
                const spent = match ? Number(match.spent) : 0;
                const pct = limit.limit > 0 ? (spent / limit.limit) * 100 : 0;
                return { ...limit, spent, pct, idx };
            });
            setCategoryLimitTrackers(trackers);
        } catch {
            // Fallback: compute client-side if API fails
            const trackers = categoryLimits.map((limit, idx) => {
                const { start, end } = getPeriodWindow(limit.interval);
                const spent = entries.filter((e: any) => {
                    if (e.type !== 'DEBIT' || !e.date) return false;
                    if (limit.field === 'tag') {
                        const entryTags = e.tag ? e.tag.split(',').map((s: string) => s.trim()) : [];
                        if (!entryTags.some((t: string) => t.toLowerCase() === limit.category.toLowerCase())) return false;
                    } else {
                        if ((e.category || '').toLowerCase() !== limit.category.toLowerCase()) return false;
                    }
                    const [ey, em, ed] = e.date.split('-').map(Number);
                    const d = new Date(ey, em - 1, ed);
                    return d >= start && d <= end;
                }).reduce((s: number, e: any) => s + Number(e.amount), 0);
                const pct = limit.limit > 0 ? (spent / limit.limit) * 100 : 0;
                return { ...limit, spent, pct, idx };
            });
            setCategoryLimitTrackers(trackers);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, selectedList?.id, categoryLimits, entries]);

    useEffect(() => {
        fetchCategorySpending();
    }, [fetchCategorySpending]);

    // (#76) Category EOY projection — fetch from API, fall back to client-side
    const [categoryEoyProjections, setCategoryEoyProjections] = useState<
        ReturnType<typeof computeCategoryEoyProjections>
    >([]);

    useEffect(() => {
        if (!userId || !selectedList?.id) {
            setCategoryEoyProjections(computeCategoryEoyProjections(categoryLimits, entries));
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const res = await getEoyProjections(userId, selectedList.id);
                if (!cancelled && Array.isArray(res?.projections)) {
                    setCategoryEoyProjections(res.projections.map((p: any) => ({
                        category: String(p.category || ''),
                        annualBudget: Number(p.annualBudget) || 0,
                        ytdSpent: Number(p.ytdSpent) || 0,
                        projectedOnBudget: Number(p.projectedOnBudget) || 0,
                        projectedAtRate: Number(p.projectedAtRate) || 0,
                    })));
                } else if (!cancelled) {
                    setCategoryEoyProjections(computeCategoryEoyProjections(categoryLimits, entries));
                }
            } catch {
                // Fall back to client-side computation
                if (!cancelled) {
                    setCategoryEoyProjections(computeCategoryEoyProjections(categoryLimits, entries));
                }
            }
        })();
        return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, selectedList?.id, categoryLimits, entries]);

    // Spend by category evolution (for line chart + table)
    // Stats computations
    const monthlyData = useMemo(() => {
        const map: Record<string, { month: string; income: number; spending: number }> = {};
        entries.forEach((e: any) => {
            if (!e.date) return;
            const key = e.date.slice(0, 7); // "YYYY-MM" — string slice avoids UTC shift
            const [yr, mo] = key.split('-').map(Number);
            const label = new Date(yr, mo - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
            if (!map[key]) map[key] = { month: label, income: 0, spending: 0 };
            if (e.type === 'CREDIT') map[key].income += Number(e.amount);
            else map[key].spending += Number(e.amount);
        });
        return Object.keys(map).sort().map(k => ({
            ...map[k],
            income: Math.round(map[k].income * 100) / 100,
            spending: Math.round(map[k].spending * 100) / 100,
        }));
    }, [entries]);

    // Income vs Spending: bar 1 = income, bar 2 = stacked (all entries + unfilled budget gaps)
    // For each month, budget remaining = sum of per-category gaps where entries < limit
    const incomeVsSpendingData = useMemo(() => {
        const debits = entries.filter((e: any) => e.type === 'DEBIT' && e.date);

        return monthlyData.map(d => {
            // Get the month key back from label for filtering entries
            const monthDebits = debits.filter((e: any) => {
                const [yr, mo] = e.date.split('-').map(Number);
                const label = new Date(yr, mo - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
                return label === d.month;
            });

            // Per-category: compute how much of each limit is unfilled
            let budgetRemaining = 0;
            categoryLimits.forEach(limit => {
                const monthlyLimitAmount = limit.interval === 'weekly' ? limit.limit * 52 / 12
                    : limit.interval === 'monthly' ? limit.limit
                    : limit.interval === 'quarterly' ? limit.limit / 3
                    : limit.limit / 12;

                const categorySpend = monthDebits
                    .filter((e: any) => {
                        if (limit.field === 'tag') {
                            const tags = e.tag ? e.tag.split(',').map((s: string) => s.trim()) : [];
                            return tags.some((t: string) => t.toLowerCase() === limit.category.toLowerCase());
                        }
                        return (e.category || '').toLowerCase() === limit.category.toLowerCase();
                    })
                    .reduce((s: number, e: any) => s + Number(e.amount), 0);

                budgetRemaining += Math.max(0, monthlyLimitAmount - categorySpend);
            });

            return {
                month: d.month,
                income: d.income,
                entries: Math.round(d.spending * 100) / 100,
                budgetRemaining: Math.round(budgetRemaining * 100) / 100,
            };
        });
    }, [entries, monthlyData, categoryLimits]);

    const statsMetrics = useMemo(() => {
        const totalIncome   = inflows.reduce((s, e) => s + Number(e.amount), 0);
        const totalSpending = bills.reduce((s, e) => s + Number(e.amount), 0);
        const net = totalIncome - totalSpending;
        const months = new Set(
            entries.filter((e: any) => e.date).map((e: any) => e.date.slice(0, 7))
        ).size || 1;
        return { totalIncome, totalSpending, net, avgMonthlySpending: totalSpending / months };
    }, [entries, inflows, bills]);

    const toDateStr = (d: Date) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

    return (
        <Box className={classes.tabPanel} style={{ overflowY: 'auto' }}>
            {/* Metric cards */}
            <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 12 }}>
                {[
                    { label: 'Total Income',        value: `+${fmt(statsMetrics.totalIncome)}`,           color: '#16a34a' },
                    { label: 'Total Spending',       value: fmt(-statsMetrics.totalSpending),              color: '#dc2626' },
                    { label: 'Net',                  value: fmt(statsMetrics.net),                         color: statsMetrics.net >= 0 ? '#16a34a' : '#dc2626' },
                    { label: 'Avg Monthly Spend',    value: fmt(-statsMetrics.avgMonthlySpending),         color: '#1e293b' },
                ].map(card => (
                    <Paper key={card.label} elevation={0} style={{ padding: '14px 16px', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                        <Typography style={{ fontSize: '0.65rem', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1 }}>{card.label}</Typography>
                        <Typography style={{ fontSize: '1.15rem', fontWeight: 700, color: card.color, marginTop: 4 }}>{card.value}</Typography>
                    </Paper>
                ))}
            </Box>

            {/* ── Section 1: Category Budget Trackers ── */}
            <Paper elevation={0} style={{ borderRadius: 8, border: '1px solid #e2e8f0', padding: '12px 16px' }}>
                <Box display="flex" alignItems="center" style={{ marginBottom: 12 }}>
                    <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569', flexGrow: 1 }}>Category Budget Limits</Typography>
                    <Button
                        size="small"
                        color="primary"
                        variant="outlined"
                        startIcon={<Add />}
                        style={{ fontSize: '0.72rem', textTransform: 'none', padding: '2px 10px' }}
                        onClick={() => {
                            setLimitDialogEditIdx(null);
                            setLimitDialogField('category');
                            setLimitDialogCategory('');
                            setLimitDialogAmount('');
                            setLimitDialogInterval('monthly');
                            setShowLimitDialog(true);
                        }}
                    >
                        Add Limit
                    </Button>
                </Box>
                {categoryLimitTrackers.length === 0 ? (
                    <Typography style={{ fontSize: '0.78rem', color: '#94a3b8', textAlign: 'center', padding: '16px 0' }}>
                        No category limits set. Click "Add Limit" to track spending against a budget.
                    </Typography>
                ) : (
                    <Box style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {categoryLimitTrackers.map((tracker, i) => {
                            const barColor = tracker.pct >= 100 ? '#dc2626' : tracker.pct >= 75 ? '#f59e0b' : '#16a34a';
                            return (
                                <Box key={i} style={{ cursor: 'pointer' }} onClick={() => {
                                    // (#75) Navigate to Entries tab with category filter pre-set
                                    const { start, end } = getPeriodWindow(tracker.interval);
                                    setEntryCategoryFilter(tracker.field === 'tag' ? 'all' : tracker.category);
                                    setEntryTypeFilter('DEBIT');
                                    setEntryDateFrom(toDateStr(start));
                                    setEntryDateTo(toDateStr(end));
                                    setEntrySearch('');
                                    setEntryTagFilter(tracker.field === 'tag' ? tracker.category : 'all');
                                    onNavigateToEntries();
                                }}>
                                    <Box display="flex" alignItems="center" style={{ marginBottom: 4 }}>
                                        <span className={classes.categoryDot} style={{ backgroundColor: categoryColor(tracker.category) }} />
                                        <Typography style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e293b', flexGrow: 1 }}>
                                            {tracker.category}
                                            <span style={{ fontSize: '0.68rem', fontWeight: 400, color: '#94a3b8', marginLeft: 5 }}>
                                                ({tracker.field === 'tag' ? 'tag' : 'category'})
                                            </span>
                                        </Typography>
                                        <Chip
                                            label={tracker.interval}
                                            size="small"
                                            style={{ fontSize: '0.65rem', height: 18, marginRight: 8, backgroundColor: '#f1f5f9', color: '#64748b' }}
                                        />
                                        <Typography style={{ fontSize: '0.78rem', color: '#475569', marginRight: 8 }}>
                                            {fmt(tracker.spent)} / {fmt(tracker.limit)}
                                        </Typography>
                                        <IconButton size="small" style={{ padding: 2 }} onClick={(e) => {
                                            e.stopPropagation();
                                            setLimitDialogEditIdx(tracker.idx);
                                            setLimitDialogField(tracker.field ?? 'category');
                                            setLimitDialogCategory(tracker.category);
                                            setLimitDialogAmount(String(tracker.limit));
                                            setLimitDialogInterval(tracker.interval);
                                            setShowLimitDialog(true);
                                        }}>
                                            <Edit style={{ fontSize: '0.9rem', color: '#94a3b8' }} />
                                        </IconButton>
                                        <IconButton size="small" style={{ padding: 2 }} onClick={(e) => {
                                            e.stopPropagation();
                                            const updated = categoryLimits.filter((_, idx) => idx !== tracker.idx);
                                            updateListLimitsSignal.dispatch({ categoryLimits: updated });
                                        }}>
                                            <Delete style={{ fontSize: '0.9rem', color: '#94a3b8' }} />
                                        </IconButton>
                                    </Box>
                                    <Box style={{ backgroundColor: '#f1f5f9', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                                        <Box style={{ width: `${Math.min(tracker.pct, 100)}%`, height: '100%', backgroundColor: barColor, borderRadius: 4, transition: 'width 0.3s ease' }} />
                                    </Box>
                                    <Box display="flex" alignItems="center" style={{ marginTop: 2 }}>
                                        <Typography style={{ fontSize: '0.68rem', color: barColor, fontWeight: 600, flexGrow: 1 }}>
                                            {Math.round(tracker.pct)}% used
                                        </Typography>
                                        <Typography style={{ fontSize: '0.62rem', color: '#94a3b8' }}>
                                            Click to view transactions
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                )}
            </Paper>

            {/* ── Section 1a: Category EOY Projection — Dual Model ── */}
            {categoryEoyProjections.length > 0 && (
                <Paper elevation={0} style={{ borderRadius: 8, border: '1px solid #e2e8f0', padding: '12px 16px' }}>
                    <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: 12 }}>
                        Category Projection to End of Year
                    </Typography>
                    <Box style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                        {categoryEoyProjections.map((proj, i) => {
                            const maxVal = Math.max(proj.annualBudget, proj.projectedAtRate, proj.projectedOnBudget);
                            const overOnBudget = proj.projectedOnBudget - proj.annualBudget;
                            const overAtRate   = proj.projectedAtRate  - proj.annualBudget;
                            return (
                                <Box key={i}>
                                    <Box display="flex" alignItems="center" style={{ marginBottom: 4 }}>
                                        <span className={classes.categoryDot} style={{ backgroundColor: categoryColor(proj.category) }} />
                                        <Typography style={{ fontSize: '0.8rem', fontWeight: 600, color: '#1e293b', flexGrow: 1 }}>
                                            {proj.category}
                                        </Typography>
                                        <Typography style={{ fontSize: '0.72rem', color: overAtRate > 0 ? '#dc2626' : '#16a34a', fontWeight: 700 }}>
                                            {fmt(Math.abs(overAtRate))} {overAtRate > 0 ? 'over' : 'under'} at rate
                                        </Typography>
                                    </Box>
                                    {/* Stacked bar: YTD (solid) + "at rate" (semi) + budget line */}
                                    <Box style={{ backgroundColor: '#f1f5f9', borderRadius: 4, height: 12, overflow: 'hidden', position: 'relative' }}>
                                        <Box style={{
                                            position: 'absolute', left: 0, top: 0, height: '100%',
                                            width: `${maxVal > 0 ? Math.min((proj.ytdSpent / maxVal) * 100, 100) : 0}%`,
                                            backgroundColor: categoryColor(proj.category),
                                            borderRadius: 4, zIndex: 3,
                                        }} />
                                        <Box style={{
                                            position: 'absolute', left: 0, top: 0, height: '100%',
                                            width: `${maxVal > 0 ? Math.min((proj.projectedAtRate / maxVal) * 100, 100) : 0}%`,
                                            backgroundColor: '#fbbf24', opacity: 0.5,
                                            borderRadius: 4, zIndex: 2,
                                        }} />
                                        <Box style={{
                                            position: 'absolute', left: 0, top: 0, height: '100%',
                                            width: `${maxVal > 0 ? Math.min((proj.projectedOnBudget / maxVal) * 100, 100) : 0}%`,
                                            backgroundColor: '#86efac', opacity: 0.5,
                                            borderRadius: 4, zIndex: 1,
                                        }} />
                                        {maxVal > 0 && (
                                            <Box style={{
                                                position: 'absolute', top: 0, bottom: 0,
                                                left: `${Math.min((proj.annualBudget / maxVal) * 100, 100)}%`,
                                                width: 2, backgroundColor: '#1e293b', zIndex: 4,
                                            }} />
                                        )}
                                    </Box>
                                    <Box style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 4, marginTop: 4 }}>
                                        <Typography style={{ fontSize: '0.65rem', color: '#64748b' }}>
                                            YTD: <span style={{ fontWeight: 700, color: '#3b82f6' }}>{fmt(proj.ytdSpent)}</span>
                                        </Typography>
                                        <Typography style={{ fontSize: '0.65rem', color: '#64748b' }}>
                                            On budget: <span style={{ fontWeight: 700, color: overOnBudget > 0 ? '#dc2626' : '#16a34a' }}>{fmt(proj.projectedOnBudget)}</span>
                                        </Typography>
                                        <Typography style={{ fontSize: '0.65rem', color: '#64748b' }}>
                                            At rate: <span style={{ fontWeight: 700, color: overAtRate > 0 ? '#dc2626' : '#16a34a' }}>{fmt(proj.projectedAtRate)}</span>
                                        </Typography>
                                        <Typography style={{ fontSize: '0.65rem', color: '#64748b' }}>
                                            Budget: <span style={{ fontWeight: 700, color: '#475569' }}>{fmt(proj.annualBudget)}</span>
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                    <Box style={{ marginTop: 10, display: 'flex', gap: 16, fontSize: '0.62rem', color: '#94a3b8' }}>
                        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                            <Box style={{ width: 12, height: 8, backgroundColor: '#64748b', borderRadius: 2 }} /> YTD Spent
                        </Box>
                        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                            <Box style={{ width: 12, height: 8, backgroundColor: '#fbbf24', opacity: 0.5, borderRadius: 2 }} /> At current rate
                        </Box>
                        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                            <Box style={{ width: 12, height: 8, backgroundColor: '#86efac', opacity: 0.5, borderRadius: 2 }} /> If on budget
                        </Box>
                        <Box display="flex" alignItems="center" style={{ gap: 4 }}>
                            <Box style={{ width: 2, height: 8, backgroundColor: '#1e293b' }} /> Annual budget
                        </Box>
                    </Box>
                </Paper>
            )}

            {/* Monthly income vs spending (entries + budget limits) */}
            {!isMobile && monthlyData.length > 0 && (
                <Paper elevation={0} style={{ borderRadius: 8, border: '1px solid #e2e8f0', padding: '12px 16px' }}>
                    <Typography style={{ fontSize: '0.78rem', fontWeight: 700, color: '#475569', marginBottom: 8 }}>Monthly Income vs Spending</Typography>
                    <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={incomeVsSpendingData} margin={{ top: 4, right: 8, bottom: 28, left: 44 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                            <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b' }} angle={-40} textAnchor="end" interval={0} height={44} />
                            <YAxis tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={(v: number) => fmt(v)} />
                            <RTooltip formatter={(v: any, name: string) => {
                                const label = name === 'income' ? 'Income'
                                    : name === 'entries' ? 'Actual Spending'
                                    : 'Budget Remaining';
                                return [fmt(v as number), label];
                            }} />
                            <Legend formatter={(v: string) =>
                                v === 'income' ? 'Income'
                                : v === 'entries' ? 'Actual Spending'
                                : 'Budget Remaining'
                            } wrapperStyle={{ fontSize: '0.72rem' }} />
                            <Bar dataKey="income" fill="#16a34a" radius={[3, 3, 0, 0]} />
                            <Bar dataKey="entries" stackId="spending" fill="#dc2626" />
                            <Bar dataKey="budgetRemaining" stackId="spending" fill="#fca5a5" radius={[3, 3, 0, 0]} />
                        </BarChart>
                    </ResponsiveContainer>
                </Paper>
            )}

            {/* ── Category limit add/edit dialog ── */}
            <Dialog open={showLimitDialog} onClose={() => setShowLimitDialog(false)} maxWidth="xs" fullWidth>
                <DialogTitle>{limitDialogEditIdx !== null ? 'Edit Limit' : 'Add Category Limit'}</DialogTitle>
                <DialogContent>
                    <Box display="flex" flexDirection="column" style={{ gap: 16, paddingTop: 8 }}>
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel>Match by</InputLabel>
                            <Select
                                value={limitDialogField}
                                onChange={e => { setLimitDialogField(e.target.value as 'category' | 'tag'); setLimitDialogCategory(''); }}
                                label="Match by"
                                style={{ fontSize: '0.85rem' }}
                            >
                                <MenuItem value="category" style={{ fontSize: '0.85rem' }}>Category</MenuItem>
                                <MenuItem value="tag"      style={{ fontSize: '0.85rem' }}>Tag</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel>{limitDialogField === 'tag' ? 'Tag' : 'Category'}</InputLabel>
                            <Select
                                value={limitDialogCategory}
                                onChange={e => setLimitDialogCategory(e.target.value as string)}
                                label={limitDialogField === 'tag' ? 'Tag' : 'Category'}
                                style={{ fontSize: '0.85rem' }}
                            >
                                {(limitDialogField === 'tag' ? allTags : allCategories).map((val: string) => (
                                    <MenuItem key={val} value={val} style={{ fontSize: '0.85rem' }}>{val}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <TextField
                            label="Limit amount ($)"
                            type="number"
                            value={limitDialogAmount}
                            onChange={e => setLimitDialogAmount(e.target.value)}
                            variant="outlined"
                            size="small"
                            fullWidth
                            InputProps={{ inputProps: { step: '0.01', min: '0' } }}
                        />
                        <FormControl variant="outlined" size="small" fullWidth>
                            <InputLabel>Period</InputLabel>
                            <Select
                                value={limitDialogInterval}
                                onChange={e => setLimitDialogInterval(e.target.value as any)}
                                label="Period"
                                style={{ fontSize: '0.85rem' }}
                            >
                                {(['weekly', 'monthly', 'quarterly', 'yearly'] as const).map(p => (
                                    <MenuItem key={p} value={p} style={{ fontSize: '0.85rem', textTransform: 'capitalize' }}>
                                        {p.charAt(0).toUpperCase() + p.slice(1)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowLimitDialog(false)} style={{ color: '#64748b' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={!limitDialogCategory || !limitDialogAmount || parseFloat(limitDialogAmount) <= 0}
                        onClick={() => {
                            const newLimit: CategoryLimit = {
                                category: limitDialogCategory,
                                limit:    parseFloat(limitDialogAmount),
                                interval: limitDialogInterval,
                                field:    limitDialogField,
                            };
                            let updated: CategoryLimit[];
                            if (limitDialogEditIdx !== null) {
                                updated = categoryLimits.map((l, i) => i === limitDialogEditIdx ? newLimit : l);
                            } else {
                                updated = [...categoryLimits, newLimit];
                            }
                            updateListLimitsSignal.dispatch({ categoryLimits: updated });
                            setShowLimitDialog(false);
                        }}
                    >
                        Save
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};
